// src/components/TopicDetail.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Container,
  Center,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { getContentFileUrl } from "../utils/fileUtils";
import { trackDocumentOpen, trackPageView } from "../utils/analyticsTracker";

const TopicDetail = () => {
  const { id } = useParams();
  const [content, setContent] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // ดึงข้อมูลหมวดหมู่
        const categoryResponse = await apiService({
          method: 'GET',
          url: `/categories/${id}`
        });
        const categoryData = categoryResponse.data;
        setCategory(categoryData);

        // ดึงข้อมูลเนื้อหาของหมวดหมู่นี้
        const contentResponse = await apiService({
          method: 'GET',
          url: `/content?category_id=${id}&is_active=true&limit=100`
        });
        
        // API response structure: { success: true, data: { content: [...], pagination: {...} } }
        const contentArray = contentResponse.data?.content || contentResponse.data || [];
        setContent(contentArray);

        // บันทึก page view ของหมวดหมู่ (พร้อมชื่อหมวด)
        trackPageView(
          `/topicdetail/${id}`,
          categoryData?.title ? `หมวดหมู่: ${categoryData.title}` : `หมวดหมู่ #${id}`
        );

      } catch (error) {
        console.error("Error fetching data from API", error);
        toast({
          title: "ไม่สามารถโหลดข้อมูลได้",
          description: error.message || "โปรดตรวจสอบการเชื่อมต่อ API",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setContent([]);
        setCategory(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, toast]);

  // จัดกลุ่มเนื้อหาตาม year_type (ปีงบประมาณ) เป็นหลัก
  const groupedData = Array.isArray(content) ? content.reduce((acc, item) => {
    // ใช้ year_type เป็นหลัก หรือ content_type ถ้าไม่มี year_type
    const typeKey = item.year_type || item.content_type || "อื่นๆ";
    if (!acc[typeKey]) acc[typeKey] = [];
    acc[typeKey].push(item);
    return acc;
  }, {}) : {};

  return (
    <Box w="100%" bg="white" borderRadius="2xl" py={10}>
      <Center>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <HStack justify="space-between">
              {category && (
                <Heading textAlign="center" color="teal.500">
                  {category.title}
                </Heading>
              )}
              <Button colorScheme="teal" variant="outline" size="sm" onClick={() => navigate("/")}>
                ย้อนกลับ
              </Button>
            </HStack>

            <Box bg="teal.500" w="50%" h="3px" />

            {loading ? (
              <Center py={10}><Spinner /></Center>
            ) : Object.keys(groupedData).length > 0 ? (
              Object.keys(groupedData)
                .sort((a, b) => {
                  // ถ้าเป็นตัวเลข (ปี พ.ศ.) ให้เรียงจากมากไปน้อย (ใหม่ไปเก่า)
                  const an = !isNaN(a) && a.length === 4;
                  const bn = !isNaN(b) && b.length === 4;
                  if (an && bn) return Number(b) - Number(a);
                  if (an && !bn) return -1;
                  if (!an && bn) return 1;
                  return a.localeCompare(b);
                })
                .map((type) => (
                  <Box key={type}>
                    <Heading size="lg" mb={4}>
                      {type ? (String(type).startsWith("25") ? `ปีพ.ศ. ${type}` : type) : ""}
                    </Heading>
                    <VStack spacing={4} align="stretch">
                      {groupedData[type]
                        .sort((a, b) => b.id - a.id) // เรียงตาม id จากมากไปหาน้อย
                        .map((item, idx) => (
                        <HStack
                          key={`${item.id}-${item.content_type}-${item.title}-${idx}`}
                          borderWidth="1px"
                          borderRadius="md"
                          p={4}
                          bg="gray.50"
                          _hover={{ bg: "gray.100" }}
                        >
                          <FontAwesomeIcon icon={faFileLines} size="lg" />
                          <VStack align="start" flex="1">
                            <Text fontWeight="bold">{item.title}</Text>
                            {item.description && (
                              <Text fontSize="sm" color="gray.600">
                                 {item.description}
                              </Text>
                            )}
                          </VStack>
                          {item.file_url ? (
                            <Button
                              colorScheme="teal"
                              size="sm"
                              onClick={() => {
                                trackDocumentOpen({
                                  contentId: item.id,
                                  categoryId: Number(id) || item.category_id,
                                  title: item.title,
                                });
                                window.open(getContentFileUrl(item.file_url), '_blank', 'noopener,noreferrer');
                              }}
                            >
                              เปิดไฟล์
                            </Button>
                          ) : (
                            <Text color="gray.500">ไม่มีไฟล์</Text>
                          )}
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                ))
            ) : (
              <Text textAlign="center" color="gray.500">
                ไม่พบข้อมูลสำหรับหัวข้อนี้
              </Text>
            )}
          </VStack>
        </Container>
      </Center>
    </Box>
  );
};

export default TopicDetail;
